// Reads and writes for lyh_stories (see the 20260728190000 migration).

import { useCallback, useEffect, useState } from 'react'
import { supabase } from './supabase'
import { signPhotoUrls } from './storyPhotos'

/** How the writer chose to appear to readers. */
export type StoryVisibility = 'anonymous' | 'anonymous_contact' | 'public'

export type StoryStatus = 'pending' | 'approved' | 'rejected'

/**
 * A story as the CLIENT can ever see it. There is deliberately no `email` field:
 * the column carries no SELECT grant for anon or authenticated, so nothing that
 * runs in a browser can read it, whoever is signed in. Andrew gets a contact
 * address through the insert trigger's push instead.
 */
export interface StoryRow {
  id: string
  body: string
  visibility: StoryVisibility
  display_name: string | null
  business: string | null
  status: StoryStatus
  created_at: string
  /** Object paths in the story-photos bucket. Readable off any approved row,
      but a path is not an image: rendering one needs a signed URL, and signing
      goes through a policy that only clears approved stories. See
      lib/storyPhotos. */
  photos: string[] | null
}

/** What the public form sends. status is left off entirely; anon has no grant on
    it, so the column defaults to 'pending' and cannot be talked out of it. The
    id IS sent, because the photos were uploaded into a folder named after it
    before this row existed. */
export interface StoryDraft {
  id: string
  body: string
  visibility: StoryVisibility
  display_name: string | null
  business: string | null
  email: string | null
  photos: string[] | null
}

// Always name the columns. Never '*': the star expands to every column
// including `email`, which anon may not read, so a star select is rejected
// outright. That is the intended behaviour, not something to work around.
const COLS = 'id,body,visibility,display_name,business,status,created_at,photos'

/** Approved stories, newest first. Anything pending is invisible here by RLS as
    well as by the filter, so a missed filter could not leak an unreviewed one. */
export function useApprovedStories() {
  const [stories, setStories] = useState<StoryRow[]>([])
  const [loading, setLoading] = useState(true)

  const refetch = useCallback(async () => {
    setLoading(true)
    const { data, error } = await supabase
      .from('lyh_stories')
      .select(COLS)
      .eq('status', 'approved')
      .order('created_at', { ascending: false })
    if (error) console.error('load approved stories', error)
    setStories((data as StoryRow[]) ?? [])
    setLoading(false)
  }, [])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { stories, loading, refetch }
}

/** Admin: the review queue. RLS only returns these rows to Andrew's email. */
export function usePendingStories(enabled: boolean) {
  const [pendingStories, setPendingStories] = useState<StoryRow[]>([])

  const refetch = useCallback(async () => {
    if (!enabled) {
      setPendingStories([])
      return
    }
    const { data, error } = await supabase
      .from('lyh_stories')
      .select(COLS)
      .eq('status', 'pending')
      .order('created_at', { ascending: false })
    if (error) console.error('load pending stories', error)
    setPendingStories((data as StoryRow[]) ?? [])
  }, [enabled])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { pendingStories, refetch }
}

/**
 * Signed URLs for every photo across a set of stories, in one request.
 *
 * Signed rather than public because the bucket is private and the SELECT policy
 * is what decides who may see a photo - so this hook returns nothing for a
 * pending story unless the caller is Andrew, and the public page therefore
 * cannot render an unreviewed image even if a path leaked into it.
 *
 * URLs last an hour, which outlives any realistic time on the page; the map is
 * re-signed whenever the set of paths changes.
 */
export function useStoryPhotoUrls(stories: StoryRow[]): Record<string, string> {
  const [urls, setUrls] = useState<Record<string, string>>({})

  // A stable key so this does not re-sign on every render of the same list.
  const key = stories
    .flatMap((s) => s.photos ?? [])
    .sort()
    .join('|')

  useEffect(() => {
    const paths = key ? key.split('|') : []
    if (paths.length === 0) {
      setUrls({})
      return
    }
    let live = true
    signPhotoUrls(paths).then((map) => {
      if (live) setUrls(map)
    })
    return () => {
      live = false
    }
  }, [key])

  return urls
}

/** Who to print above a story. Only 'public' ever shows a name; both anonymous
    modes read the same to a reader, which is the whole promise of the middle
    one. */
export function storyByline(story: StoryRow): string {
  if (story.visibility !== 'public' || !story.display_name) return 'Anonymous'
  return story.business
    ? `${story.display_name}, ${story.business}`
    : story.display_name
}
